import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { legalConfig } from '@/config';

function a11yProps(index: number) {
  return {
    id: `legal-tab-${index}`,
    'aria-controls': `legal-tabpanel-${index}`,
  } as const;
}

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`legal-tabpanel-${index}`}
      aria-labelledby={`legal-tab-${index}`}
      sx={{ pt: 2 }}
    >
      {children}
    </Box>
  );
}

export default function LegalFooterDialog() {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={handleOpen}
        underline="hover"
        color="inherit"
        aria-haspopup="dialog"
      >
        Privacidad y aviso legal
      </Link>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="legal-dialog-title"
      >
        <DialogTitle id="legal-dialog-title">
          Privacidad y aviso legal
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Última actualización: {legalConfig.lastUpdated}. Este texto es una
            plantilla informativa y no constituye asesoramiento jurídico.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Tabs
            value={tab}
            onChange={(_event, nextValue: number) => setTab(nextValue)}
            aria-label="Secciones legales"
          >
            <Tab label="Privacidad" {...a11yProps(0)} />
            <Tab label="Aviso legal" {...a11yProps(1)} />
          </Tabs>

          <TabPanel value={tab} index={0}>
            <Typography variant="h6" gutterBottom>
              1) Responsable del tratamiento (RGPD)
            </Typography>
            <Typography paragraph>
              El <strong>Responsable del tratamiento</strong> de los datos
              tratados mediante esta aplicación es <strong>{legalConfig.controllerName}</strong>,
              con domicilio en <strong>{legalConfig.controllerAddress}</strong> y
              email de contacto <strong>{legalConfig.controllerEmail}</strong>.
            </Typography>
            <Typography paragraph>
              Esta política describe el tratamiento de datos personales que
              pueda producirse cuando los usuarios utilizan la aplicación e
              introducen información (incluyendo datos de terceros) en el CRM.
            </Typography>

            <Typography variant="h6" gutterBottom>
              2) Datos tratados
            </Typography>
            <Typography paragraph>
              La aplicación puede tratar, entre otros, los siguientes datos:
            </Typography>
            <Typography paragraph>
              - Datos de cuenta y autenticación (p. ej. identificadores, email,
              nombre, tokens/sesiones).{'\n'}
              - Datos introducidos por los usuarios en el CRM (p. ej. clientes,
              leads, notas, actividades, reuniones, tareas, correos).{'\n'}
              - Datos técnicos necesarios para el funcionamiento y seguridad
              (p. ej. registros de errores, IP, timestamps).
            </Typography>

            <Typography variant="h6" gutterBottom>
              3) Finalidad del tratamiento
            </Typography>
            <Typography paragraph>
              Los datos se tratan para: prestar el servicio de CRM; permitir el
              acceso y gestión de usuarios; sincronizar/gestionar correo si se
              configura la integración; atender solicitudes de soporte; y
              garantizar la seguridad y el correcto funcionamiento.
            </Typography>

            <Typography variant="h6" gutterBottom>
              4) Base jurídica
            </Typography>
            <Typography paragraph>
              La base jurídica del tratamiento puede ser: la ejecución de un
              contrato o medidas precontractuales (art. 6.1.b RGPD) para prestar
              el servicio; el interés legítimo (art. 6.1.f) para seguridad,
              prevención de fraude y mejora; y, cuando corresponda, el
              consentimiento (art. 6.1.a) para funcionalidades o integraciones
              concretas.
            </Typography>

            <Typography variant="h6" gutterBottom>
              5) Conservación
            </Typography>
            <Typography paragraph>
              Los datos se conservan mientras exista una cuenta activa o mientras
              sea necesario para prestar el servicio. El Responsable podrá
              conservar copias de seguridad y registros técnicos durante plazos
              limitados por motivos de seguridad y cumplimiento.
            </Typography>

            <Typography variant="h6" gutterBottom>
              6) Destinatarios, encargados y terceros
            </Typography>
            <Typography paragraph>
              Los datos pueden tratarse por proveedores que prestan servicios
              necesarios (p. ej. hosting, correo, infraestructura). Si se
              habilitan integraciones con terceros, dichos proveedores tratarán
              datos conforme a sus propias políticas y condiciones.
            </Typography>

            <Typography variant="h6" gutterBottom>
              7) Derechos de las personas
            </Typography>
            <Typography paragraph>
              Puedes ejercer tus derechos de acceso, rectificación, supresión,
              oposición, limitación y portabilidad contactando en{' '}
              <strong>{legalConfig.controllerEmail}</strong>.
            </Typography>
            <Typography paragraph>
              También tienes derecho a presentar una reclamación ante la autoridad
              de control competente; en España, la Agencia Española de Protección
              de Datos (AEPD).
            </Typography>

            <Typography variant="h6" gutterBottom>
              8) Cookies y almacenamiento local
            </Typography>
            <Typography paragraph>
              La aplicación puede utilizar cookies técnicas y almacenamiento
              local estrictamente necesarios para autenticación, seguridad y
              preferencias. No se utiliza publicidad comportamental salvo que se
              indique expresamente en esta política.
            </Typography>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="h6" gutterBottom>
              1) Titular del sitio/aplicación
            </Typography>
            <Typography paragraph>
              Titular: <strong>{legalConfig.controllerName}</strong>. Domicilio:{' '}
              <strong>{legalConfig.controllerAddress}</strong>. Contacto:{' '}
              <strong>{legalConfig.controllerEmail}</strong>.
            </Typography>
            {legalConfig.controllerId ? (
              <Typography paragraph>
                NIF/CIF: <strong>{legalConfig.controllerId}</strong>.
              </Typography>
            ) : (
              <Typography paragraph color="text.secondary">
                (Si aplica) NIF/CIF y datos registrales.
              </Typography>
            )}

            <Typography variant="h6" gutterBottom>
              2) Uso permitido y responsabilidades
            </Typography>
            <Typography paragraph>
              El usuario se compromete a utilizar la aplicación de forma lícita,
              a no introducir contenidos ilícitos y a mantener la
              confidencialidad de sus credenciales. El usuario es responsable de
              los datos que introduce y de disponer de base legal para tratarlos.
            </Typography>

            <Typography variant="h6" gutterBottom>
              3) Limitación de responsabilidad
            </Typography>
            <Typography paragraph>
              Salvo obligación legal o pacto expreso, {legalConfig.controllerName}{' '}
              no será responsable de daños indirectos, pérdida de datos o
              interrupciones derivadas del uso del software. El Responsable debe
              implementar copias de seguridad y medidas de seguridad adecuadas a
              su contexto.
            </Typography>

            <Typography variant="h6" gutterBottom>
              4) Contacto del proveedor
            </Typography>
            <Typography paragraph>
              Proveedor del software:{' '}
              <Link
                href={legalConfig.providerUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {legalConfig.providerName}
              </Link>
            </Typography>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
